// ============================================
// JOB DETAIL PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Building2, DollarSign, Calendar, Users, Ship, Briefcase, Clock, Loader2 } from 'lucide-react';
import { PageHeader, StatusBadge } from '@/components';
import { adminApi } from '@/services/api';
import { formatDate, formatNumber } from '@/utils';

export const Route = createFileRoute('/_authenticated/jobs/$id/')({
  component: JobDetailPage,
});

function JobDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: jobData, isLoading } = useQuery({
    queryKey: ['admin', 'jobs', id],
    queryFn: () => adminApi.getJob(id),
  });

  // Handle both direct and wrapped response formats
  const job = (jobData as any)?.data || jobData;

  // Durum değiştir mutation
  const statusMutation = useMutation({
    mutationFn: (status: string) => adminApi.updateJob(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'jobs', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">İlan bulunamadı</p>
        <Link to="/jobs" className="text-maritime-600 hover:underline mt-2 inline-block">
          İlanlara Dön
        </Link>
      </div>
    );
  }

  const departmentLabels: Record<string, string> = {
    DECK: 'Güverte',
    ENGINE: 'Makine',
    ELECTRICAL: 'Elektrik',
    CATERING: 'Catering',
    OTHER: 'Diğer',
  };

  const vesselTypeLabels: Record<string, string> = {
    BULK_CARRIER: 'Bulk Carrier',
    TANKER: 'Tanker',
    CONTAINER: 'Container',
    GENERAL_CARGO: 'General Cargo',
    RORO: 'Ro-Ro',
    PASSENGER: 'Passenger',
    OFFSHORE: 'Offshore',
    TUG: 'Tug',
    YACHT: 'Yacht',
    FISHING: 'Fishing',
    OTHER: 'Diğer',
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="İlan Detayı"
        description={job.title}
        actions={
          <div className="flex gap-2">
            <Link
              to="/jobs"
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri
            </Link>
            <Link
              to={`/jobs/${id}/edit` as any}
              className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700"
            >
              <Edit className="h-4 w-4" />
              Düzenle
            </Link>
          </div>
        }
      />

      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold">{job.title}</h2>
            <div className="flex items-center gap-2 mt-1 text-gray-600">
              <Building2 className="h-4 w-4" />
              <span>{job.company?.name || '-'}</span>
            </div>
          </div>
          <StatusBadge status={job.status} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <Briefcase className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Departman</p>
              <p className="font-medium">{departmentLabels[job.department] || job.department}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <Ship className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Gemi Tipi</p>
              <p className="font-medium">{vesselTypeLabels[job.vesselType] || job.vesselType}</p>
            </div>
          </div>

          {job.vesselName && (
            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Ship className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Gemi Adı</p>
                <p className="font-medium">{job.vesselName}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <DollarSign className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Maaş</p>
              <p className="font-medium">
                {job.salaryMin && job.salaryMax
                  ? `${formatNumber(job.salaryMin)} - ${formatNumber(job.salaryMax)} ${job.salaryCurrency}`
                  : job.salaryMin
                    ? `${formatNumber(job.salaryMin)}+ ${job.salaryCurrency}`
                    : '-'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <Clock className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Sözleşme Süresi</p>
              <p className="font-medium">{job.contractDuration ? `${job.contractDuration} ay` : '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <Briefcase className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Min. Deneyim</p>
              <p className="font-medium">{job.experienceMin ? `${job.experienceMin} yıl` : '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <Users className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Başvuru Sayısı</p>
              <p className="font-medium">{job.applicationCount || 0}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <Calendar className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Yayın Tarihi</p>
              <p className="font-medium">{job.publishedAt ? formatDate(job.publishedAt) : '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-maritime-100 rounded-full">
              <Calendar className="h-5 w-5 text-maritime-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Bitiş Tarihi</p>
              <p className="font-medium">{job.expiresAt ? formatDate(job.expiresAt) : '-'}</p>
            </div>
          </div>
        </div>

        {job.description && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-medium mb-2">Açıklama</h3>
            <p className="text-gray-600 whitespace-pre-wrap">{job.description}</p>
          </div>
        )}
      </div>

      {/* Admin Aksiyonları */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Admin Aksiyonları</h3>
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">İlan Durumu</label>
            <div className="flex items-center gap-2">
              <select
                value={job.status}
                onChange={(e) => statusMutation.mutate(e.target.value)}
                disabled={statusMutation.isPending}
                className="h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500 disabled:opacity-50"
              >
                <option value="DRAFT">Taslak</option>
                <option value="ACTIVE">Aktif</option>
                <option value="PAUSED">Duraklatıldı</option>
                <option value="CLOSED">Kapatıldı</option>
              </select>
              {statusMutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-maritime-600" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
